/**
 * What a `save_firm_settings` approval will change for everyone at the firm:
 * firm instructions in full (every agent reads them), any other section as
 * one row per field it sets.
 */
import type { FC } from "react";

import type { FirmSettingsPreview } from "@/features/agents/types";

import { titleCase } from "../../header/uscisModel";

const FIELD_LABELS: Record<string, string> = {
  zipcode: "ZIP code",
  postal_code: "Postal code",
  unit_type: "Unit type",
  unit_number: "Unit number",
  logo_attachment_id: "Logo",
  agent_ask_every_write: "Ask before every change",
  agent_event_wakes: "Event wake-ups",
  default_exhibit_scheme: "Exhibit labels",
  default_title_template: "Exhibit titles",
  folder_instructions: "Folder structure",
  title_instructions: "Title preferences",
  default_packet_settings: "New packets",
  contents_order: "Order",
  case_insights_automation: "Case Insights",
  keep_current: "Keep current",
  run_analyses: "Research memos"
};

const fieldLabel = (key: string): string =>
  FIELD_LABELS[key] ?? titleCase(key.replace(/_/g, " "));

const valueText = (value: unknown): string => {
  if (value === true) return "On";
  if (value === false) return "Off";
  if (value == null || value === "") return "Cleared";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (Array.isArray(value)) return value.map(valueText).join(", ");
  if (typeof value === "object")
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${fieldLabel(k)}: ${valueText(v)}`)
      .join("; ");
  return String(value);
};

export const FirmSettingsApprovalPreview: FC<{
  preview: FirmSettingsPreview;
}> = ({ preview }) => {
  if (typeof preview.text === "string") {
    return (
      <div
        className="max-h-60 overflow-auto rounded-md bg-fill-1 px-2.5 py-2 text-sm whitespace-pre-wrap text-fg"
        data-testid="firm-settings-preview-text"
      >
        {preview.text || "Empty: the firm instructions would be cleared."}
      </div>
    );
  }
  const rows = Object.entries(preview.fields ?? {});
  if (!rows.length) {
    return (
      <div className="rounded-md bg-fill-1 px-2.5 py-2 text-sm text-fg-3">
        Keeps {preview.label} as they are and marks them reviewed.
      </div>
    );
  }
  return (
    <ul
      className="divide-y divide-line-1 rounded-md bg-fill-1 text-sm"
      data-testid="firm-settings-preview-fields"
    >
      {rows.map(([key, value]) => (
        <li key={key} className="flex gap-3 px-2.5 py-1.5">
          <span className="w-28 shrink-0 text-fg-3">{fieldLabel(key)}</span>
          <span className="min-w-0 flex-1 break-words text-fg">
            {valueText(value)}
          </span>
        </li>
      ))}
    </ul>
  );
};
